import { useLocalStorage } from '@mantine/hooks'
import { useState, useEffect } from 'react'
import { TableColumn } from './GenericTable'
import superjson from 'superjson'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const isDateString = (value: unknown): boolean => {
  if (typeof value !== 'string' || !value || value === 'N/A') return false

  const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/
  if (isoDatePattern.test(value)) return true

  const europeanDatePattern = /^\d{1,2}\.\d{1,2}\.\d{4}, \d{1,2}:\d{2}$/
  if (europeanDatePattern.test(value)) return true

  return dayjs(value).isValid()
}

/**
 * Parses a date string to a timestamp for comparison using dayjs
 * Returns null if the value is not a valid date
 */
const parseDateValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === 'N/A') return null
  if (typeof value === 'string') {
    let date = dayjs(value)
    if (date.isValid()) return date.valueOf()

    date = dayjs(value, 'DD.MM.YYYY, HH:mm', true)
    if (date.isValid()) return date.valueOf()

    date = dayjs(value)
    return date.isValid() ? date.valueOf() : null
  }
  return null
}

export function useGenericTable<T>({
  defaultSortField,
  defaultSortDirection = 'desc',
  itemsPerPage = 20,
  tableKey,
  columns,
  filteredData,
}: {
  defaultSortField: keyof T
  defaultSortDirection?: 'asc' | 'desc'
  itemsPerPage?: number
  tableKey: string
  columns: TableColumn<T>[]
  searchDebounceMs?: number
  filteredData: T[]
}) {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<keyof T>(defaultSortField)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection)
  const [tableColumns, setTableColumns] = useLocalStorage<TableColumn<T>[]>({
    key: tableKey,
    defaultValue: columns,
    getInitialValueInEffect: true,
    serialize: superjson.stringify,
    deserialize: (value: string | undefined) => {
      if (!value) return columns
      const storedColumns = superjson.parse(value) as TableColumn<T>[]
      const columnsMap = new Map(columns.map(col => [col.key, col]))
      return storedColumns.map((storedCol: TableColumn<T>) => {
        const originalCol = columnsMap.get(storedCol.key)
        return {
          ...storedCol,
          label: originalCol?.label ?? storedCol.label,
          tooltip: originalCol?.tooltip ?? storedCol.tooltip,
          render: originalCol?.render,
          csvValue: originalCol?.csvValue,
          sortFunction: originalCol?.sortFunction,
        }
      })
    },
  })

  const columnLabelsKey = columns.map(c => `${String(c.key)}:${c.label}`).join('|')

  useEffect(() => {
    const columnsMap = new Map(columns.map(col => [col.key, col]))
    setTableColumns(prev =>
      prev.map(storedCol => {
        const originalCol = columnsMap.get(storedCol.key)
        if (!originalCol) return storedCol
        return {
          ...storedCol,
          label: originalCol.label,
          tooltip: originalCol.tooltip,
          csvValue: originalCol.csvValue,
        }
      })
    )
    // columns/setTableColumns intentionally omitted; columnLabelsKey is the change signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnLabelsKey])

  // Reset current page when the number of filtered results changes (e.g. search/filter applied)
  useEffect(() => {
    setCurrentPage(1)
  }, [filteredData.length])

  const handleSort = (field: keyof T) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handlePageChange = (page: number) => setCurrentPage(page)

  // Get visible columns for rendering
  const visibleColumns = tableColumns.filter(col => col.visible)

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    // Check if there's a custom sort function for the current sort field
    const column = tableColumns.find(col => col.key === sortField)
    if (column?.sortFunction) {
      const result = column.sortFunction(a, b)
      return sortDirection === 'asc' ? result : -result
    }

    // Default sorting logic
    const aValue = a[sortField]
    const bValue = b[sortField]

    // Handle null/undefined values - put them at the end
    if (aValue === null || aValue === undefined || aValue === 'N/A') return 1
    if (bValue === null || bValue === undefined || bValue === 'N/A') return -1

    // Check if this is a date column (either explicitly marked or both values are date strings)
    const isDateColumn = column?.type === 'date' || (isDateString(aValue) && isDateString(bValue))

    if (isDateColumn) {
      const aDate = parseDateValue(aValue)
      const bDate = parseDateValue(bValue)

      // Handle invalid dates - put them at the end
      if (aDate === null) return 1
      if (bDate === null) return -1

      // Compare dates numerically
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }

    return 0
  })

  // Clamp page to valid range so we never show a blank page after data shrinks
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage))
  const safePage = Math.min(currentPage, totalPages)

  // Paginate data
  const paginatedData =
    itemsPerPage === Infinity ? sortedData : sortedData.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage)

  return {
    // State
    currentPage: safePage,
    sortField,
    sortDirection,
    tableColumns,
    visibleColumns,
    filteredData,
    sortedData,
    paginatedData,

    // Actions
    setCurrentPage,
    setSortField,
    setSortDirection,
    setTableColumns,

    // Handlers
    handleSort,
    handlePageChange,
  }
}
